import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, ParamMap, RouterOutlet } from '@angular/router';
import { NavigationHeaderComponent } from './components/navigation-header/navigation-header.component';
import { AuthService } from './services/auth.service';
import { AsyncPipe } from '@angular/common';
import { environment } from '../environments/environment';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ThemeService } from './services/theme.service';
import { ApolloClientService } from './services/apollo-client.service';
import { AuthenticationMutations } from './graphql/mutations/authentication.mutation';
import { GraphqlUserDataOutput } from './graphql/models/output/user-data.output.model';
import { convertGraphqlUserDataOutputToUserData } from './graphql/mappers/user-data.mappers';
@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    NavigationHeaderComponent,
    AsyncPipe,
    ToastModule,
    CardModule,
    ButtonModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'Web Analytics';
  public isProcessingAuthentication = signal<boolean>(false);
  private destroyRef = inject(DestroyRef);
  private processingToken = false;

  params$: Observable<ParamMap> = this.route.queryParamMap;

  // public underMaintenance$ = this.ldService.launchDarklyFlags$.pipe(
  // 	map(flags => {
  // 		if (environment.environment === TargetEnvironment.Hero) {
  // 			return flags['hero-under-maintenance'];
  // 		} else {
  // 			return flags['under-maintenance'];
  // 		}
  // 	}),
  // );
  constructor(
    public authService: AuthService,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private themeService: ThemeService,
    private apolloClientService: ApolloClientService,
  ) {}

  /**
   * Authentication Flow - Bare Bones Version
   *
   * This flow handles one-time token authentication with the following steps:
   * 1. Check if user has a session (stored in localStorage)
   * 2. If no session, check for token in URL query params
   * 3. If token exists, consume it via GraphQL mutation to get session
   * 4. Store session data in localStorage
   * 5. If no token and no session, redirect to external auth page
   *
   * Key Points:
   * - Uses combineLatest to react to both URL params and userData changes
   * - Prevents duplicate token consumption with processingToken flag
   * - Parses token from URLSearchParams (not Angular's params$) to handle edge cases
   * - Template conditionally shows app content only when session exists
   */
  ngOnInit(): void {
    this.themeService.getTheme();

    combineLatest({
      params: this.params$,
      userData: this.authService.userData$,
    })
      .pipe(
        // Only re-run when sessionID actually changes
        distinctUntilChanged(
          (prev, curr) => prev.userData.sessionID === curr.userData.sessionID,
        ),
        switchMap((data) => {
          // Parse token from URL (URLSearchParams is more reliable than params$)
          const urlParams = new URLSearchParams(window.location.search);
          const token = urlParams.get(environment.oneTimeTokenName);

          // No session? Try to authenticate
          if (!data.userData.sessionID && token) {
            // Prevent duplicate token consumption
            if (this.processingToken) {
              return of(false);
            }
            this.processingToken = true;
            this.isProcessingAuthentication.set(true);

            // Handle auth failure case
            if (token === 'noAuth') {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to sign in. No authentication token provided.',
              });
              this.processingToken = false;
              return of(false);
            }

            // Consume the one-time token to get session
            return this.apolloClientService.authLayerClient.pipe(
              switchMap((client) =>
                client.mutate<{ consumeOneTimeToken: GraphqlUserDataOutput }>({
                  mutation: AuthenticationMutations.consumeOneTimeToken,
                  variables: { token: token },
                }),
              ),
              map((result) =>
                result.data?.consumeOneTimeToken
                  ? convertGraphqlUserDataOutputToUserData(
                      result.data.consumeOneTimeToken,
                    )
                  : undefined,
              ),
              map((data) => {
                if (data?.sessionID) {
                  // Store session and clean up URL
                  this.authService.updateSession(data);
                  this.authService.removeSpecificQueryParams([
                    'sessionId',
                    'username',
                    'company',
                    environment.oneTimeTokenName,
                  ]);
                  this.processingToken = false;
                } else {
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail:
                      'Failed to retrieve user data. Please sign in again.',
                  });
                  this.processingToken = false;
                }
                return false;
              }),
              tap({
                error: (err) => {
                  console.error('Error consuming token:', err);
                  this.processingToken = false;
                },
              }),
            );
          }
          // Already have session, do nothing
          return of(false);
        }),
        tap(() => this.isProcessingAuthentication.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
